import { useEffect, useRef } from "react";
import { makeStyles } from "tss-react/mui";
import { TIMELINE_WIDTH } from "@miplatform/constants";
import { Box, Divider, Typography } from "@mui/material";
import { TimeValue } from "../types";

export function CurrentTimeIndicator({
  time,
  width = 0,
  visible = true,
  zoom = 1,
}: Readonly<CurrentTimeIndicatorProps>) {
  const ref = useRef<HTMLDivElement>(null);
  const position = time?.position ?? 0;
  const value = time?.value;

  useEffect(() => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [zoom]);

  const { classes } = useStyles({ top: position * zoom, width });

  return (
    visible && (
      <>
        <Box className={classes.textContainer} ref={ref}>
          <Box className={classes.textBox}>
            <Typography className={classes.timeText} variant="body2">
              {value?.format("h:mm")}
            </Typography>
          </Box>
        </Box>
        <Box
          className={classes.timelineContainer}
          data-testid="current-time-indicator"
        >
          <Divider className={classes.timeline} />
        </Box>
      </>
    )
  );
}

const useStyles = makeStyles<{ top: number; width: number }>()(
  (theme, { top, width }) => ({
    textContainer: {
      display: "flex",
      position: "absolute",
      alignItems: "center",
      top: top - 4,
    },
    textBox: {
      backgroundColor: theme.palette.red.main,
      fontWeight: theme.fontWeight.semiBold,
      borderRadius: 4,
      width: TIMELINE_WIDTH,
      textAlign: "center",
      position: "sticky",
      left: 0,
      zIndex: theme.zIndex.appBar + 1,
    },
    timelineContainer: {
      display: "flex",
      position: "absolute",
      top,
      left: TIMELINE_WIDTH,
      alignItems: "center",
    },
    timeText: {
      fontSize: theme.fontSize.xxsmall,
      color: theme.palette.common.white,
    },
    timeline: {
      width,
      borderColor: theme.palette.red.main,
      borderTopWidth: 1,
      borderRadius: "0 4px 4px 0",
      zIndex: theme.zIndex.appBar,
      left: TIMELINE_WIDTH,
    },
  })
);

export interface CurrentTimeIndicatorProps {
  time?: TimeValue | null;
  width?: number;
  visible?: boolean;
  zoom?: number;
}
